import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getReview, postUpdateReview } from '../services/SoTellUsApiService';
import { Box, Button, Center, Rating, Textarea, TextInput, Text, Loader, Radio } from '@mantine/core';
import { ReviewResponseData } from '../types/ApiTypes';
import { SystemContext } from '../contexts/SystemContext';
import ErrorNotification from '../components/ErrorNotification';

export default function UpdateReview() {
  const { reviewid, token} = useParams();
  const [review, setReview] = useState<ReviewResponseData | null >(null);
  const [initialReview, setInitialReview] = useState<ReviewResponseData | null >(null);
  const [postLoading, setPostLoading] = useState<boolean>(false);
  const [getLoading, setGetLoading] = useState<boolean>(true);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const { setSystem } = useContext(SystemContext);
  const [errorOpen, setErrorOpen] = useState<boolean>(false);
  const [starValueMap, setStarValueMap] = useState<number[]>([5,4,3,2,1]);

  const handleReviewChange = (field: string, value : string | number) => {
        setReview(prev => prev ? { ...prev, [field]: value } : prev);   
    };

  useEffect(() => {
    const fetchReview = async () => {
      if (!reviewid || !token) {
        setGetLoading(false)
        return;
      } 
      try {
        const data = await getReview(reviewid, token, setSystem);
        setReview(data);
        setInitialReview(data);
        setStarValueMap(starValueMap.filter((n) => n >= data?.rating))
        setGetLoading(false);
      } catch (error) {
        //console.error(error);
      } finally {
        setGetLoading(false);
      }
    };
    fetchReview();
  }, [reviewid]);

  const handleSetError = (error : string) => {
    setError(error);
    setErrorOpen(true);
  }

  const handleUpdateSubmit = async() => {
    const jwtToken = localStorage.getItem('jwtToken') ?? "";
    if (review) {
        setPostLoading(true);
        try {
            await postUpdateReview(review, jwtToken);
            setShowThankYou(true);
        } catch(err : unknown) {
             const message =
                err instanceof Error
                    ? err.message
                    : typeof err === 'string'
                    ? err
                    : JSON.stringify(err);
               handleSetError(message);
        } finally {
            setPostLoading(false)
        }
    }
  }

    // controlled example
    const RadioStarGroup = ({ stars }: { stars: number }) => {
      return (
        <Radio.Group
          name="rating"
          value={stars.toString()}
          onChange={(value: string) => handleReviewChange("rating", Number(value))}
        >
          {starValueMap.map((star) => (
            <RadioStar key={star} starValue={star} />
          ))}
        </Radio.Group>
      )
    };

    // each Radio must also speak string
    const RadioStar = ({ starValue }: { starValue: number }) => (
      <Radio
        value={starValue.toString()}
        my={10}
        pos="relative"
        label={
          <div style={{ position: "relative" }}>
            <Button
              p={0}
              pos="absolute"
              onClick={() => handleReviewChange("rating", starValue)}
              style={{ backgroundColor: "transparent", zIndex: 2 }}
            />
            <Rating readOnly mt={0} size={30} value={starValue} />
          </div>
        }
        styles={{ body: { alignItems: "center" } }}
      />
    );


  const hasChanges = JSON.stringify(review) !== JSON.stringify(initialReview);

    if (getLoading) {
        return (
        <Center style={{ height: 300 }}>
            <Loader />
        </Center>
        );
    }

    if (!review) {
        return <Box>No Review Found</Box>;
    }


  return (
    <Box className="main-box" mb={20}>
       <ErrorNotification
          opened={errorOpen}
          onClose={() => setErrorOpen(false)}
          message={error}
          autoCloseDelay={5}
      />
        <Box w="100%">
            <Center>
                <img style={{width: 400, height: 'auto'}} src={review?.client_logo}/>
            </Center>
            </Box>
        {(!showThankYou && !review?.has_updated) ? 
        <Box mt={20} w="100%">
               <Text fz={12} c="dimmed" ta="center">Change Star Rating</Text>
               <Center>
                    <RadioStarGroup stars={review?.rating} />
                </Center>
               <TextInput
                label="First Name"
                defaultValue={review?.firstname ?? ""}
                onChange={(e) => handleReviewChange("firstname", e.target.value)}
                mb={10}
                />
                   <TextInput
                label="Last Name"
                defaultValue={review?.lastname ?? ""}
                onChange={(e) => handleReviewChange("lastname", e.target.value)}
                mb={10}
                />
             <Textarea
                w="100%"
                label="Review"
                defaultValue={review?.review ?? ""}
                onChange={(e) => handleReviewChange("review", e.target.value)}
                mb={10}
                autosize
                minRows={10}
            />
            <Button disabled={!hasChanges} onClick={handleUpdateSubmit}loading={postLoading} color='green' w="100%">Update Review</Button>
        </Box>
        : <Text size="lg">Thank you for updating your review!</Text>}
    </Box>
  );
}
